import { useTranslation } from "react-i18next";

import type { PlayerRecap, Recap } from "../../services/http.js";
import { Slices, TallyStats } from "../../components/game/TallySheet.js";
import { cn } from "../../lib/utils.js";

/**
 * The recap itself — everybody's numbers, then what was played.
 *
 * The numbers themselves are `components/game/TallySheet.tsx`, shared with the
 * career screen: the same seven figures about the same person, one game apart.
 */
export function RecapBoard({ recap }: { recap: Recap }) {
  const { t } = useTranslation();

  return (
    <>
      <section aria-label={t("recap.players")} className="flex flex-col gap-3">
        {recap.players.map((player) => (
          <PlayerCard key={player.profile_id} player={player} />
        ))}
      </section>

      <section aria-label={t("recap.everyRound")}>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-white/50">
          {t("recap.whatYouPlayed")}
        </h2>
        <ol className="flex flex-col gap-1">
          {recap.timeline.map((line, index) => (
            <li
              key={`${line.round_no}-${index}`}
              className={cn(
                "flex items-baseline gap-3 rounded-lg px-3 py-2",
                line.voided ? "bg-coin-400/10" : "bg-stage-900",
              )}
            >
              <span className="w-16 shrink-0 text-lg font-semibold tabular-nums text-flash-400">
                {line.year ?? "—"}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">
                  {line.title ?? t("recap.goneTrack")}
                </span>
                <span className="block truncate text-xs text-white/50">
                  {line.artist ?? "—"}
                  {line.placed_correctly.length > 0 && (
                    <>
                      {" "}
                      ·{" "}
                      {t("recap.placedBy", {
                        names: line.placed_correctly.join(", "),
                      })}
                    </>
                  )}
                </span>
              </span>
              {line.voided && (
                // In words. A struck-through row would read as a rendering
                // glitch, and ADR 0009's do-over has to look deliberate.
                <span className="shrink-0 text-xs text-coin-400">
                  {t("recap.didntCount")}
                </span>
              )}
            </li>
          ))}
        </ol>
      </section>
    </>
  );
}

function PlayerCard({ player }: { player: PlayerRecap }) {
  const { t } = useTranslation();

  return (
    <article
      data-testid="player-recap"
      className={cn(
        "rounded-2xl border-2 p-4",
        player.won ? "border-flash-500 bg-flash-500/5" : "border-white/10",
      )}
    >
      <header className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="min-w-0 truncate text-xl font-semibold">
          {player.display_name}
          {player.won && <span className="text-flash-400"> — {t("recap.won")}</span>}
          {player.eliminated && (
            <span className="text-white/50"> — {t("recap.out")}</span>
          )}
        </h2>
        <span className="shrink-0 text-sm tabular-nums text-white/60">
          {t("side.cards", { count: player.cards })} ·{" "}
          {t("recap.coins", { count: player.coins })}
        </span>
      </header>

      <TallyStats tally={player.tally} />

      <Slices label={t("recap.byDecade")} buckets={player.by_decade} />
      <Slices
        label={t("recap.byGenre")}
        buckets={player.by_genre}
        unknown={player.genre_unknown}
      />
    </article>
  );
}
